import { useMemo, useState } from 'react';
import { addAction, addError } from '@shared/utils/logger';
import { getIsValidLatitudeAndLongitude } from '../AccommodationsConsole.utils';
import { AccommodationsConsolePropertiesV2Query, useAccommodationsConsolePropertiesV2Query } from '@graphql/generated';
import { AccommodationsConsoleState, JoyHotelAccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { useSearchAccommodationsQueryMetadataPayload } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { isValidLatLngLocation } from '@shared/utils/locationValidation';
import { useEventInfo } from '@shared/utils/eventInfo';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';

interface UseAccommodationsConsoleListingsArgs {
  state: AccommodationsConsoleState;
  skip?: boolean;
  isInitialLoad: boolean;
}

type AccommodationConsoleQueryProperty = AccommodationsConsolePropertiesV2Query['searchExtendedAccommodationProperties']['properties'][number];

type ValidAccommodationConsoleQueryProperty = Merge<
  AccommodationConsoleQueryProperty,
  { location: { latitude: number; longitude: number }; address: NonNullable<AccommodationConsoleQueryProperty['address']> }
>;

const isValidProperty = (property: AccommodationConsoleQueryProperty): property is ValidAccommodationConsoleQueryProperty => {
  return isValidLatLngLocation(property.location) && !!property.address;
};

export const useRegionalHotels = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, skip, isInitialLoad } = args;
  const { eventHandle } = useEventInfo();

  const [cachedListings, setCachedListings] = useState<NonNullable<AccommodationsConsolePropertiesV2Query['searchExtendedAccommodationProperties']>['properties']>([]);

  const { isPreparingSearchMetadataPayload, searchMetadata } = useSearchAccommodationsQueryMetadataPayload();

  const hasValidLocationSearchCoordinates =
    !!state.circularRegionSearch && getIsValidLatitudeAndLongitude(state.circularRegionSearch.latitude, state.circularRegionSearch.longitude);

  const locationPayload = hasValidLocationSearchCoordinates
    ? {
        latitude: state.circularRegionSearch?.latitude,
        longitude: state.circularRegionSearch?.longitude,
      }
    : null;

  const {
    data,
    loading: isFetchingListings,
    error,
  } = useAccommodationsConsolePropertiesV2Query({
    variables: {
      payload: {
        eventHandle: eventHandle!,
        ...(isInitialLoad
          ? {}
          : {
              customSearchParams: {
                location: locationPayload!,
                stayDates: {
                  checkIn: {
                    high: state.searchDates.checkin,
                    low: state.searchDates.checkin,
                  },
                  checkOut: {
                    high: state.searchDates.checkout,
                    low: state.searchDates.checkout,
                  },
                },
                occupancies: state.occupancies,
                metadata: searchMetadata,
              },
            }),
      },
    },

    onCompleted: data => {
      if (data?.searchExtendedAccommodationProperties?.properties) {
        setCachedListings(data.searchExtendedAccommodationProperties.properties);
      }

      if (!data?.searchExtendedAccommodationProperties?.properties?.length) {
        addAction('Search extended accommodations properties with circularRegionSearch api with empty results (AccommodationsConsole)', {
          context: 'searchExtendedAccommodationProperties',
          circularRegionSearch: state.circularRegionSearch,
        });
      }
    },
    onError: error => {
      addError(error, {
        source: 'Search extended accommodations properties with circularRegionSearch api failed (AccommodationsConsole)',
        context: 'searchExtendedAccommodationProperties',
        errorMessage: error.message,
        circularRegionSearch: state.circularRegionSearch,
      });
    },
    skip: isInitialLoad ? !eventHandle : !hasValidLocationSearchCoordinates || isPreparingSearchMetadataPayload || skip,
    batchMode: 'off',
  });

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  const listings = useMemo((): Array<JoyHotelAccommodationListingDTO> => {
    return (
      data?.searchExtendedAccommodationProperties?.properties
        ?.filter(isValidProperty)
        .sort((a, b) => b.score - a.score)
        .map(listing => ({
          id: listing.id,
          type: 'standardHotel',
          searchScore: listing.score,
          joyPlaceId: listing.joyPlaceId,
          isFavorite: false,
          name: listing.name,
          description: null,
          isSoldOutForSelectedDates: !!extractHotelRedirects(listing.redirectMechanisms)?.hotelSearchPageUrl,
          userNote: null,
          address: {
            street: listing.address.street,
            city: listing.address.city,
            country: listing.address.country,
          },
          location: {
            lat: listing.location.latitude,
            lng: listing.location.longitude,
          },
          starRating: listing.starRating,
          userRating: listing.userRating
            ? {
                ratingValue: listing.userRating.ratingValue,
                ratingCount: listing.userRating.ratingCount,
              }
            : null,
          mediaAssets: {
            isLoading: false,
            photos: listing.photos,
          },
          facilities: listing.facilities,
          pricingInfo: {
            isLoading: false,
            values: listing.pricingInfo ? mapHotelRoomPrice(listing.pricingInfo) : null,
          },
        })) || []
    );
  }, [data, mapHotelRoomPrice]);

  return {
    /**
     * Can be used to retain maps points while loading new data
     */
    cachedListings,
    listings,
    isFetchingListings,
    error,
  };
};
